import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Grid,
  Chip,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  Divider,
  InputAdornment
} from '@mui/material';
import {
  Add,
  Search,
  MoreVert,
  Edit,
  Delete,
  Visibility,
  Agriculture,
  LocalHospital,
  Scale,
  LocationOn,
  Info,
  ChevronRight
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const RAZAS_POR_ESPECIE = {
  bovino: ['Terreña', 'Frisona', 'Monchina', 'Pirenaica', 'Betizu'],
  ovino: ['Latxa Cara Negra', 'Latxa Cara Rubia', 'Carranzana'],
  caprino: ['Azpi Gorri', 'Alpina', 'Saanen'],
  porcino: ['Euskal Txerria', 'Ibérico', 'Duroc']
};

const UBICACIONES_DISPONIBLES = ['Pastizal Norte', 'Establo Principal', 'Zona de Partos', 'Cuarentena', 'Caserío Viejo'];

const Animales = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    especie: 'todas',
    sexo: 'todos',
    salud: 'todos',
    ubicacion: 'todas'
  });
  const [selectedAnimal, setSelectedAnimal] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [animalDialogOpen, setAnimalDialogOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [formData, setFormData] = useState({
    crotal: '',
    especie: 'bovino',
    raza: '',
    sexo: 'hembra',
    fecha_nacimiento: new Date().toISOString().split('T')[0],
    peso_actual: '',
    estado_salud: 'sano',
    ubicacion_actual: '',
    observaciones: ''
  });

  // Fetch animals
  const { data: animalesData, isLoading } = useQuery('animales', async () => {
    const response = await axios.get('/api/animales');
    return response.data;
  });

  // Mutations
  const createMutation = useMutation(
    async (data) => await axios.post('/api/animales', data),
    { onSuccess: () => { queryClient.invalidateQueries('animales'); setAnimalDialogOpen(false); } }
  );

  const updateMutation = useMutation(
    async (data) => await axios.put(`/api/animales/${data.id}`, data),
    { onSuccess: () => { queryClient.invalidateQueries('animales'); setAnimalDialogOpen(false); } }
  );

  const deleteMutation = useMutation(
    async (id) => await axios.delete(`/api/animales/${id}`),
    { onSuccess: () => { queryClient.invalidateQueries('animales'); } }
  );

  const generateCrotal = () => {
    const random = Math.floor(100000000000 + Math.random() * 899999999999);
    setFormData({ ...formData, crotal: `ES48${random}` });
  };

  const handleMenuClick = (event, animal) => { setAnchorEl(event.currentTarget); setSelectedAnimal(animal); };
  const handleMenuClose = () => { setAnchorEl(null); };

  const handleViewAnimal = () => { setDialogOpen(true); handleMenuClose(); };
  const handleEditAnimal = () => { if (selectedAnimal) { setFormData(selectedAnimal); setIsEditing(true); setAnimalDialogOpen(true); } handleMenuClose(); };
  const handleDeleteAnimal = () => { if (selectedAnimal) { if (window.confirm('¿Eliminar este animal?')) deleteMutation.mutate(selectedAnimal.id); } handleMenuClose(); };

  if (isLoading) return <Box sx={{ p: 4, textAlign: 'center' }}><Typography variant="h6">Cargando inventario...</Typography></Box>;

  const animales = Array.isArray(animalesData) ? animalesData : [];
  
  const filteredAnimales = animales.filter(animal => {
    const matchesSearch = (animal.crotal || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (animal.raza || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesEspecie = filters.especie === 'todas' || animal.especie === filters.especie;
    const matchesSexo = filters.sexo === 'todos' || animal.sexo === filters.sexo;
    const matchesSalud = filters.salud === 'todos' || animal.estado_salud === filters.salud;
    const matchesUbicacion = filters.ubicacion === 'todas' || animal.ubicacion_actual === filters.ubicacion;
    
    return matchesSearch && matchesEspecie && matchesSexo && matchesSalud && matchesUbicacion;
  });

  const stats = {
    total: animales.length,
    sanos: animales.filter(a => a.estado_salud === 'sano').length,
    tratamiento: animales.filter(a => a.estado_salud === 'tratamiento' || a.estado_salud === 'observacion').length,
    enfermos: animales.filter(a => a.estado_salud === 'enfermo').length
  };

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, mt: 8, bgcolor: 'var(--bg-app)', minHeight: '100vh' }}>
      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 800, color: 'var(--navy-main)', letterSpacing: '-0.02em' }}>
            Inventario de Ganado
          </Typography>
          <Typography variant="body1" sx={{ color: 'var(--slate-light)', fontWeight: 500 }}>
            Listado maestro y control individual de especímenes.
          </Typography>
        </Box>
        <Button
          className="luxury-button luxury-button-primary"
          variant="contained"
          startIcon={<Add />}
          onClick={() => { setIsEditing(false); setFormData({ crotal: '', especie: 'bovino', raza: '', sexo: 'hembra', fecha_nacimiento: new Date().toISOString().split('T')[0], peso_actual: '', estado_salud: 'sano', ubicacion_actual: '', observaciones: '' }); setAnimalDialogOpen(true); }}
        >
          Nuevo Registro
        </Button>
      </Box>

      {/* Stats Grid */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {[
          { label: 'Total Cabezas', value: stats.total, color: 'var(--navy-main)', icon: <Agriculture /> },
          { label: 'Estado Óptimo', value: stats.sanos, color: 'var(--emerald-main)', icon: <Visibility /> },
          { label: 'En Tratamiento', value: stats.tratamiento, color: 'var(--alert-gold)', icon: <LocalHospital /> },
          { label: 'Alertas Salud', value: stats.enfermos, color: 'var(--alert-crimson)', icon: <Info /> },
        ].map((s, i) => (
          <Grid item xs={12} sm={6} md={3} key={i}>
            <Card className="premium-card" sx={{ height: '100%', py: 1 }}>
              <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ bgcolor: `${s.color}15`, color: s.color }}>{s.icon}</Avatar>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 800, color: s.color }}>{s.value}</Typography>
                  <Typography variant="caption" sx={{ color: 'var(--slate-light)', fontWeight: 700, textTransform: 'uppercase' }}>{s.label}</Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Filters & Table */}
      <Card className="premium-card">
        <Box sx={{ p: 3, borderBottom: '1px solid var(--border-color)', display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            fullWidth
            placeholder="Buscar por crotal o raza..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            variant="outlined"
            size="small"
            InputProps={{
              startAdornment: <InputAdornment position="start"><Search /></InputAdornment>,
              sx: { borderRadius: '12px', bgcolor: 'var(--slate-bg)' }
            }}
          />
          <Grid container spacing={2}>
            <Grid item xs={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Especie</InputLabel>
                <Select value={filters.especie} label="Especie" onChange={(e) => setFilters({...filters, especie: e.target.value})}>
                  <MenuItem value="todas">Todas</MenuItem>
                  <MenuItem value="bovino">Bovino</MenuItem>
                  <MenuItem value="ovino">Ovino</MenuItem>
                  <MenuItem value="caprino">Caprino</MenuItem>
                  <MenuItem value="porcino">Porcino</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Sexo</InputLabel>
                <Select value={filters.sexo} label="Sexo" onChange={(e) => setFilters({...filters, sexo: e.target.value})}>
                  <MenuItem value="todos">Todos</MenuItem>
                  <MenuItem value="hembra">Hembra</MenuItem>
                  <MenuItem value="macho">Macho</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Salud</InputLabel>
                <Select value={filters.salud} label="Salud" onChange={(e) => setFilters({...filters, salud: e.target.value})}>
                  <MenuItem value="todos">Todos</MenuItem>
                  <MenuItem value="sano">Sano</MenuItem>
                  <MenuItem value="tratamiento">Tratamiento</MenuItem>
                  <MenuItem value="enfermo">Enfermo</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Ubicación</InputLabel>
                <Select value={filters.ubicacion} label="Ubicación" onChange={(e) => setFilters({...filters, ubicacion: e.target.value})}>
                  <MenuItem value="todas">Todas</MenuItem>
                  {UBICACIONES_DISPONIBLES.map(u => <MenuItem key={u} value={u}>{u}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Box>
        <TableContainer>
          <Table sx={{ minWidth: 800 }}>
            <TableHead sx={{ bgcolor: 'var(--slate-bg)' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 800, color: 'var(--navy-main)' }}>Identificación (Crotal)</TableCell>
                <TableCell sx={{ fontWeight: 800, color: 'var(--navy-main)' }}>Especie & Raza</TableCell>
                <TableCell sx={{ fontWeight: 800, color: 'var(--navy-main)' }}>Peso & Salud</TableCell>
                <TableCell sx={{ fontWeight: 800, color: 'var(--navy-main)' }}>Ubicación</TableCell>
                <TableCell align="right" sx={{ fontWeight: 800, color: 'var(--navy-main)' }}>Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredAnimales.map((animal) => (
                <TableRow key={animal.id} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Avatar sx={{ bgcolor: 'var(--forest-main)' }}>{animal.especie[0].toUpperCase()}</Avatar>
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>{animal.crotal}</Typography>
                        <Typography variant="caption" sx={{ color: 'var(--slate-light)' }}>#{animal.id}</Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{animal.raza}</Typography>
                    <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                      <Chip label={animal.sexo} size="small" variant="outlined" />
                      <Chip label={animal.especie} size="small" sx={{ bgcolor: 'var(--emerald-light)', color: 'var(--forest-main)' }} />
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>{animal.peso_actual} kg</Typography>
                    <Chip label={animal.estado_salud} size="small" 
                      sx={{ mt: 0.5, bgcolor: animal.estado_salud === 'sano' ? 'var(--alert-mint)' : '#fee2e2', color: animal.estado_salud === 'sano' ? 'var(--emerald-main)' : 'var(--alert-crimson)' }} 
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{animal.ubicacion_actual}</Typography>
                  </TableCell>
                  <TableCell align="right">
                    <IconButton onClick={(e) => handleMenuClick(e, animal)}><MoreVert /></IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Register/Edit Dialog */}
      <Dialog open={animalDialogOpen} onClose={() => setAnimalDialogOpen(false)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: '20px' } }}>
        <DialogTitle sx={{ fontWeight: 800 }}>{isEditing ? 'Editar Ficha' : 'Nuevo Registro'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <TextField fullWidth label="Crotal" value={formData.crotal} onChange={(e) => setFormData({...formData, crotal: e.target.value.toUpperCase()})} placeholder="ES48..." />
                {!isEditing && <Button variant="outlined" size="small" onClick={generateCrotal} sx={{ height: '56px' }}>Auto</Button>}
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Especie</InputLabel>
                <Select value={formData.especie} label="Especie" onChange={(e) => setFormData({...formData, especie: e.target.value, raza: ''})}>
                  <MenuItem value="bovino">Bovino</MenuItem>
                  <MenuItem value="ovino">Ovino</MenuItem>
                  <MenuItem value="caprino">Caprino</MenuItem>
                  <MenuItem value="porcino">Porcino</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Raza</InputLabel>
                <Select value={formData.raza} label="Raza" onChange={(e) => setFormData({...formData, raza: e.target.value})}>
                  {RAZAS_POR_ESPECIE[formData.especie].map(r => <MenuItem key={r} value={r}>{r}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Ubicación Actual</InputLabel>
                <Select value={formData.ubicacion_actual} label="Ubicación Actual" onChange={(e) => setFormData({...formData, ubicacion_actual: e.target.value})}>
                  {UBICACIONES_DISPONIBLES.map(u => <MenuItem key={u} value={u}>{u}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Sexo</InputLabel>
                <Select value={formData.sexo} label="Sexo" onChange={(e) => setFormData({...formData, sexo: e.target.value})}>
                  <MenuItem value="hembra">Hembra</MenuItem>
                  <MenuItem value="macho">Macho</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Estado de Salud</InputLabel>
                <Select value={formData.estado_salud} label="Estado de Salud" onChange={(e) => setFormData({...formData, estado_salud: e.target.value})}>
                  <MenuItem value="sano">Sano</MenuItem>
                  <MenuItem value="tratamiento">Tratamiento</MenuItem>
                  <MenuItem value="enfermo">Enfermo</MenuItem>
                  <MenuItem value="observacion">Observación</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth type="date" label="Fecha Nacimiento" InputLabelProps={{ shrink: true }} value={formData.fecha_nacimiento} onChange={(e) => setFormData({...formData, fecha_nacimiento: e.target.value})} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth type="number" label="Peso Actual (kg)" value={formData.peso_actual} onChange={(e) => setFormData({...formData, peso_actual: e.target.value})} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth multiline rows={3} label="Observaciones" value={formData.observaciones} onChange={(e) => setFormData({...formData, observaciones: e.target.value})} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setAnimalDialogOpen(false)}>Cancelar</Button>
          <Button variant="contained" className="luxury-button luxury-button-primary" onClick={() => isEditing ? updateMutation.mutate(formData) : createMutation.mutate(formData)}>
            {isEditing ? 'Actualizar Ficha' : 'Registrar Animal'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Details */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '24px' } }}>
        <Box sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 4 }}>
            <Avatar sx={{ width: 80, height: 80, bgcolor: 'var(--emerald-light)', color: 'var(--forest-main)' }}><Agriculture fontSize="large" /></Avatar>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 800 }}>{selectedAnimal?.crotal}</Typography>
              <Typography variant="body1" sx={{ color: 'var(--slate-light)' }}>{selectedAnimal?.raza} - {selectedAnimal?.especie}</Typography>
            </Box>
          </Box>
          <Grid container spacing={3}>
            {Object.entries({
              'Sexo': selectedAnimal?.sexo,
              'Peso': `${selectedAnimal?.peso_actual} kg`,
              'Salud': selectedAnimal?.estado_salud,
              'Ubicación': selectedAnimal?.ubicacion_actual,
              'F. Nac': selectedAnimal?.fecha_nacimiento,
              'ID': `#${selectedAnimal?.id}`
            }).map(([k, v]) => (
              <Grid item xs={6} key={k}>
                <Typography variant="caption" sx={{ fontWeight: 800, color: 'var(--slate-light)', textTransform: 'uppercase' }}>{k}</Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>{v}</Typography>
              </Grid>
            ))}
          </Grid>
          <Divider sx={{ my: 3 }} />
          <Typography variant="caption" sx={{ fontWeight: 800, color: 'var(--slate-light)', textTransform: 'uppercase' }}>Observaciones</Typography>
          <Typography variant="body2" sx={{ mt: 1, p: 2, bgcolor: 'var(--slate-bg)', borderRadius: '12px' }}>{selectedAnimal?.observaciones || 'Sin notas.'}</Typography>
          <Button fullWidth variant="outlined" sx={{ mt: 4, borderRadius: '12px' }} onClick={() => setDialogOpen(false)}>Cerrar</Button>
        </Box>
      </Dialog>

      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose} PaperProps={{ sx: { borderRadius: '12px', minWidth: '180px' } }}>
        <MenuItem onClick={handleViewAnimal} sx={{ gap: 1.5 }}><Visibility fontSize="small" /> Ver Detalles</MenuItem>
        <MenuItem onClick={handleEditAnimal} sx={{ gap: 1.5 }}><Edit fontSize="small" /> Editar Animal</MenuItem>
        <Divider />
        <MenuItem onClick={handleDeleteAnimal} sx={{ gap: 1.5, color: 'var(--alert-crimson)' }}><Delete fontSize="small" /> Eliminar</MenuItem>
      </Menu>
    </Box>
  );
};

export default Animales;
